/**
 * IXD/DSD 비교 페이지 전역 상태 스토어
 * 페이지 스코프로 동작하며, Provider 언마운트 시 상태가 초기화됩니다.
 *
 * Server state (compareResults, dsdPreview, ixdPreview 등)는 React Query로 이동.
 * Store에는 UI 선택 상태와 navigation 상태만 유지합니다.
 */

import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { FsType } from "@/apis/compare/route";
import { MatchNavigation } from "@/app/ixd-dsd-compare/types";

// ---------------------------------------------------------------------------
// Pending Navigation (cross-chunk 탐색용)
// ---------------------------------------------------------------------------

export type PendingNavigationTarget = "first-match" | "last-match";

export interface PendingNavigation {
  chunkId: string;
  target: PendingNavigationTarget;
}

// ---------------------------------------------------------------------------
// 상태 슬라이스 타입
// ---------------------------------------------------------------------------

/** 업로드된 파일 상태 */
export type CompareFilesState = {
  dsdFile: File | null;
  ixdFile: File | null;
  setDsdFile: (file: File | null) => void;
  setIxdFile: (file: File | null) => void;
  clearFiles: () => void;
};

/**
 * 선택 상태 — ID만 관리, 실제 데이터는 React Query에서 파생.
 *
 * 참고: comparisonId / activeChunkId / isCompareView(=comparisonId 존재 여부)는
 * URL 쿼리 파라미터로 이동했다. useCompareUrlState 참조.
 */
export type CompareSelectionState = {
  /** 비교 결과의 IXD report id. 편집 가능 여부의 capability gate. */
  ixdReportId: string | null;
  /** 활성 DSD chunk에 매핑된 IXD chunk(하위 선택). activeChunkId(URL)에서 파생. */
  activeIxdChunkId: string | null;
  selectedFsType: FsType;
  isSyncScroll: boolean;
  /** 사용자 의도(intent) 기반 편집 모드. capability gate(ixdReportId)와 AND로 결합. */
  isIxdEditMode: boolean;
  setIxdReportId: (id: string | null) => void;
  setActiveIxdChunkId: (id: string | null) => void;
  setSelectedFsType: (fsType: FsType) => void;
  setIsSyncScroll: (value: boolean) => void;
  setIsIxdEditMode: (value: boolean) => void;
  clearSelection: () => void;
};

/** 매칭 셀 네비게이션 상태 */
export type CompareNavigationState = {
  matchNavigation: MatchNavigation | null;
  pendingNavigation: PendingNavigation | null;
  setMatchNavigation: (nav: MatchNavigation | null) => void;
  setPendingNavigation: (nav: PendingNavigation | null) => void;
  clearNavigation: () => void;
};

// ---------------------------------------------------------------------------
// 스토어 전체 타입
// ---------------------------------------------------------------------------

export type IxdDsdCompareState = CompareFilesState &
  CompareSelectionState &
  CompareNavigationState & {
    /** 전체 상태 초기화 */
    reset: () => void;
  };

// ---------------------------------------------------------------------------
// 스토어 팩토리 및 Context
// ---------------------------------------------------------------------------

export const createIxdDsdCompareStore = () =>
  createStore<IxdDsdCompareState>()((set) => ({
    // Files
    dsdFile: null,
    ixdFile: null,
    setDsdFile: (dsdFile) => set({ dsdFile }),
    setIxdFile: (ixdFile) => set({ ixdFile }),
    clearFiles: () => set({ dsdFile: null, ixdFile: null }),

    // Selection (IDs only) — comparisonId/activeChunkId는 URL에서 관리
    ixdReportId: null,
    activeIxdChunkId: null,
    selectedFsType: "consolidated" as FsType,
    isSyncScroll: true,
    isIxdEditMode: false,
    setIxdReportId: (ixdReportId) => set({ ixdReportId }),
    setActiveIxdChunkId: (activeIxdChunkId) => set({ activeIxdChunkId }),
    setSelectedFsType: (selectedFsType) => set({ selectedFsType }),
    setIsSyncScroll: (isSyncScroll) => set({ isSyncScroll }),
    setIsIxdEditMode: (isIxdEditMode) => set({ isIxdEditMode }),
    clearSelection: () =>
      set({
        ixdReportId: null,
        activeIxdChunkId: null,
        selectedFsType: "consolidated",
        isSyncScroll: true,
        isIxdEditMode: false,
      }),

    // Navigation
    matchNavigation: null,
    pendingNavigation: null,
    setMatchNavigation: (matchNavigation) => set({ matchNavigation }),
    setPendingNavigation: (pendingNavigation) => set({ pendingNavigation }),
    clearNavigation: () =>
      set({
        matchNavigation: null,
        pendingNavigation: null,
      }),

    // Reset
    reset: () =>
      set({
        dsdFile: null,
        ixdFile: null,
        ixdReportId: null,
        activeIxdChunkId: null,
        selectedFsType: "consolidated",
        isSyncScroll: true,
        isIxdEditMode: false,
        matchNavigation: null,
        pendingNavigation: null,
      }),
  }));

export type IxdDsdCompareStore = ReturnType<typeof createIxdDsdCompareStore>;

export const IxdDsdCompareStoreContext =
  createContext<IxdDsdCompareStore | null>(null);

/** 페이지 내 전역 상태 구독. Provider 밖에서 사용 시 에러를 던집니다. */
export const useIxdDsdCompareStore = <T>(
  selector: (state: IxdDsdCompareState) => T,
): T => {
  const store = useContext(IxdDsdCompareStoreContext);
  if (!store) {
    throw new Error(
      "useIxdDsdCompareStore must be used within IxdDsdCompareStoreProvider",
    );
  }
  return useStore(store, selector);
};
